import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

interface NativeSwipeBackState {
  disabledReasons: Set<string>
  isDisabled: boolean
  disable: (reason: string) => void
  enable: (reason: string) => void
}

// Ref-counted store for the iOS WKWebView native edge-swipe-back gesture.
// Default is "not disabled" — i.e. native back-swipe is ON. Anything that
// needs to suppress the native gesture (overlays, tab-root pages with their
// own pager, modals, drawers) calls `disable('unique-key')` on mount and
// `enable('unique-key')` on unmount; the gesture stays off as long as any
// reason is active.
//
// The actual native-side toggle (nativeTabBar.setSwipeBack) is fired by an
// effect in ReactWrapper that watches `isDisabled` — keeping the bridge call
// in one place avoids races between concurrent open/close cycles.
export const useNativeSwipeBackStore = create<NativeSwipeBackState>()(
  devtools(
    (set) => ({
      disabledReasons: new Set<string>(),
      isDisabled: false,
      disable: (reason) =>
        set((state) => {
          if (state.disabledReasons.has(reason)) return state
          const next = new Set(state.disabledReasons)
          next.add(reason)
          return { disabledReasons: next, isDisabled: next.size > 0 }
        }),
      enable: (reason) =>
        set((state) => {
          if (!state.disabledReasons.has(reason)) return state
          const next = new Set(state.disabledReasons)
          next.delete(reason)
          return { disabledReasons: next, isDisabled: next.size > 0 }
        }),
    }),
    { name: 'NativeSwipeBackStore', enabled: import.meta.env.DEV },
  ),
)
